// Convenience wrappers around the GraphQL queries. Each takes the accessToken
// explicitly so callers stay honest about auth state. `refresh` is an optional
// callback the caller can pass to enable transparent token refresh on 401 —
// see epicQuery() for behavior.

import { epicQuery, TokenRefresh } from "./graphql";
import {
  BULK_CREATE_CONNECTIONS_MUTATION,
  CONNECTIONS_FOR_EMAIL_QUERY,
  CONTACT_QUERY,
  CREATE_CASE_FROM_EMAIL_MUTATION,
  EMAIL_BY_INTERNET_MESSAGE_ID_QUERY,
  RECENT_ENTITIES_QUERY,
  REIMPORT_EMAIL_FROM_OUTLOOK_MUTATION,
  RESOLVE_CONTACTS_QUERY,
  SEARCH_ALL_QUERY,
  SEARCH_ENTITIES_QUERY,
} from "./queries";
import type {
  BulkConnectionsResult,
  BulkCreateConnectionsResponse,
  ConnectionsForEmailResult,
  ContactQueryResult,
  CreateCaseFromEmailInput,
  CreateCaseFromEmailResponse,
  CreateConnectionInput,
  CreatedCaseSummary,
  EmailQueryResult,
  EntityKind,
  GqlContact,
  GqlContactResolution,
  GqlEmail,
  GqlModelConnection,
  GqlRecentEntity,
  Linkable,
  RecentEntitiesResult,
  ReimportEmailFromOutlookResponse,
  ReimportEmailResult,
  ResolveContactsResult,
  SearchAllResult,
  SearchEntitiesResult,
} from "./types";

export async function resolveContacts(
  emails: string[],
  token: string,
  refresh?: TokenRefresh
): Promise<GqlContactResolution[]> {
  if (emails.length === 0) return [];
  const data = await epicQuery<ResolveContactsResult>(
    RESOLVE_CONTACTS_QUERY,
    { emails },
    token,
    refresh
  );
  return data.resolve_contacts;
}

export async function fetchContact(
  id: string,
  token: string,
  refresh?: TokenRefresh
): Promise<GqlContact | null> {
  const data = await epicQuery<ContactQueryResult>(
    CONTACT_QUERY,
    { id },
    token,
    refresh
  );
  return data.contact;
}

export async function fetchEmailByInternetMessageId(
  internetMessageId: string,
  token: string,
  refresh?: TokenRefresh
): Promise<GqlEmail | null> {
  const data = await epicQuery<EmailQueryResult>(
    EMAIL_BY_INTERNET_MESSAGE_ID_QUERY,
    { internet_message_id: internetMessageId },
    token,
    refresh
  );
  return data.email;
}

export async function fetchConnectionsForEmail(
  emailMessageId: string,
  token: string,
  refresh?: TokenRefresh
): Promise<GqlModelConnection[]> {
  const data = await epicQuery<ConnectionsForEmailResult>(
    CONNECTIONS_FOR_EMAIL_QUERY,
    { email_message_id: emailMessageId },
    token,
    refresh
  );
  return data.connections_for_email;
}

export async function searchEntities(
  kind: EntityKind,
  q: string,
  token: string,
  limit: number = 20,
  refresh?: TokenRefresh
): Promise<Linkable[]> {
  const data = await epicQuery<SearchEntitiesResult>(
    SEARCH_ENTITIES_QUERY,
    { kind, q, limit },
    token,
    refresh
  );
  return data.search_entities;
}

/**
 * Type-agnostic search across every HasModelConnections model. Backed by
 * Scout/Typesense on the server (same engine the Filament ModelConnection
 * picker uses). Returns a mixed Linkable[] — caller dispatches via
 * entityFromLinkable.
 */
export async function searchAll(
  q: string,
  token: string,
  limit: number = 25,
  refresh?: TokenRefresh
): Promise<Linkable[]> {
  const data = await epicQuery<SearchAllResult>(
    SEARCH_ALL_QUERY,
    { q, limit },
    token,
    refresh
  );
  return data.search_all;
}

/**
 * The current user's most-recently-viewed records, deduped to one row
 * per entity (latest interaction wins). Backed by the backend's
 * user_model_interactions log via `recent_entities`. Used as the
 * default empty-state list in Add Connection.
 */
export async function fetchRecentEntities(
  token: string,
  limit: number = 10,
  refresh?: TokenRefresh,
  kind?: EntityKind
): Promise<GqlRecentEntity[]> {
  const data = await epicQuery<RecentEntitiesResult>(
    RECENT_ENTITIES_QUERY,
    { limit, direction: "DESC", kind },
    token,
    refresh
  );
  return data.recent_entities;
}

/**
 * Create N ModelConnection rows in one round-trip. Each input row is
 * (entity_one, entity_two) — the Outlook add-in always uses
 * entity_one_type=EMAIL with the open email's id, and entity_two_*
 * for each selected target. With `disallow_duplicates: true`, an
 * existing connection between the same pair is returned in
 * `connections` instead of being re-created, and `skipped_existing`
 * counts how many.
 */
export async function bulkCreateConnections(
  inputs: CreateConnectionInput[],
  token: string,
  refresh?: TokenRefresh
): Promise<BulkConnectionsResult> {
  const data = await epicQuery<BulkCreateConnectionsResponse>(
    BULK_CREATE_CONNECTIONS_MUTATION,
    { input: inputs },
    token,
    refresh
  );
  return data.bulk_create_connections;
}

/**
 * AI-seeded "create case from email" — the server uses LLM analysis on the
 * email body to fill name + remarks. ALL optional fields can be omitted
 * (and usually should be, for v1) — `email_message_id` alone is enough.
 * The resolver also auto-creates the EmailMessage↔CaseRecord connection,
 * so callers should refetch connections_for_email afterwards.
 */
export async function createCaseFromEmail(
  input: CreateCaseFromEmailInput,
  token: string,
  refresh?: TokenRefresh
): Promise<CreatedCaseSummary> {
  const data = await epicQuery<CreateCaseFromEmailResponse>(
    CREATE_CASE_FROM_EMAIL_MUTATION,
    { input },
    token,
    refresh
  );
  return data.create_case_from_email;
}

/**
 * Pull an untracked email into EPIC by its Internet-Message-Id. The backend
 * searches every mailbox over live Graph and force-imports the first copy it
 * finds — so this can block for several seconds. A "not found in any mailbox"
 * outcome is returned as `success:false` (email null), NOT thrown; the caller
 * should surface `message` in that case.
 */
export async function reimportEmailFromOutlook(
  internetMessageId: string,
  token: string,
  refresh?: TokenRefresh
): Promise<ReimportEmailResult> {
  const data = await epicQuery<ReimportEmailFromOutlookResponse>(
    REIMPORT_EMAIL_FROM_OUTLOOK_MUTATION,
    { internet_message_id: internetMessageId },
    token,
    refresh
  );
  return data.reimport_email_from_outlook;
}
